// Admin inquiries slice. Read-only: loads the submitted-inquiry list (with an
// optional status filter) and a single inquiry's full detail. List and detail
// each carry their own loading/error so the detail page can load independently
// of the list (e.g. on a direct link / refresh).
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { adminFetch, AdminAuthError } from '../api/admin-client.js';
import { clearAdminSession } from './admin-auth-slice.js';

const handleThunkError = (err, dispatch, rejectWithValue) => {
  if (err instanceof AdminAuthError) dispatch(clearAdminSession());
  return rejectWithValue(err.message);
};

export const fetchInquiries = createAsyncThunk(
  'adminInquiries/fetchList',
  async (arg, { dispatch, rejectWithValue }) => {
    try {
      const status = arg?.status;
      const query = status ? `?status=${encodeURIComponent(status)}` : '';
      const { items } = await adminFetch(`/api/admin/inquiries${query}`);
      return items;
    } catch (err) {
      return handleThunkError(err, dispatch, rejectWithValue);
    }
  },
);

export const fetchInquiry = createAsyncThunk(
  'adminInquiries/fetchOne',
  async (id, { dispatch, rejectWithValue }) => {
    try {
      const { item } = await adminFetch(`/api/admin/inquiries/${encodeURIComponent(id)}`);
      return item;
    } catch (err) {
      return handleThunkError(err, dispatch, rejectWithValue);
    }
  },
);

// Contact-gate submissions (every initial-form submission, passed or blocked).
// `arg.blocked` may be true/false to filter, or undefined for everything.
export const fetchContactSubmissions = createAsyncThunk(
  'adminInquiries/fetchSubmissions',
  async (arg, { dispatch, rejectWithValue }) => {
    try {
      const { blocked } = arg || {};
      const query = blocked === undefined ? '' : `?blocked=${blocked ? 'true' : 'false'}`;
      const { items } = await adminFetch(`/api/admin/contact-submissions${query}`);
      return items;
    } catch (err) {
      return handleThunkError(err, dispatch, rejectWithValue);
    }
  },
);

const initialState = {
  list: [],
  listLoading: false,
  listError: null,
  detail: null,
  detailLoading: false,
  detailError: null,
  submissions: [],
  submissionsLoading: false,
  submissionsError: null,
};

const adminInquiriesSlice = createSlice({
  name: 'adminInquiries',
  initialState,
  reducers: {
    clearDetail: (s) => {
      s.detail = null;
      s.detailError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInquiries.pending, (s) => {
        s.listLoading = true;
        s.listError = null;
      })
      .addCase(fetchInquiries.fulfilled, (s, a) => {
        s.listLoading = false;
        s.list = a.payload;
      })
      .addCase(fetchInquiries.rejected, (s, a) => {
        s.listLoading = false;
        s.listError = a.payload || 'Could not load inquiries.';
      })
      .addCase(fetchInquiry.pending, (s) => {
        s.detailLoading = true;
        s.detailError = null;
        s.detail = null;
      })
      .addCase(fetchInquiry.fulfilled, (s, a) => {
        s.detailLoading = false;
        s.detail = a.payload;
      })
      .addCase(fetchInquiry.rejected, (s, a) => {
        s.detailLoading = false;
        s.detailError = a.payload || 'Could not load this inquiry.';
      })
      .addCase(fetchContactSubmissions.pending, (s) => {
        s.submissionsLoading = true;
        s.submissionsError = null;
      })
      .addCase(fetchContactSubmissions.fulfilled, (s, a) => {
        s.submissionsLoading = false;
        s.submissions = a.payload;
      })
      .addCase(fetchContactSubmissions.rejected, (s, a) => {
        s.submissionsLoading = false;
        s.submissionsError = a.payload || 'Could not load form submissions.';
      });
  },
});

export const { clearDetail } = adminInquiriesSlice.actions;
export default adminInquiriesSlice.reducer;
